import { graphql, useStaticQuery } from 'gatsby';
import React, { FunctionComponent } from 'react';
import { useScreenSize } from '../../../hooks/useScreenSize';
import { ArticlePreview as ArticlePreviewType } from '../../../types/articlePreview';
import { PagePath } from '../../../types/common';
import ArticleCard from '../../ArticleCard';
import ArticlePreview from '../../ArticlePreview';
import { InternalLinkIcon } from '../../Icons';
import InternalLink from '../../InternalLink';
import HighlichtCard from './HighlightCard';
import './styles.scss';

type NewsHomeProps = {
  data: {
    pageReference: PagePath;
    title?: string | null;
    description?: string | null;
    cta?: string | null;
    highlightTitle?: string | null;
    highlight?: ArticlePreviewType[] | null;
    htmlId: string | null;
  };
}

const NewsHome: FunctionComponent<NewsHomeProps> = ({ data }) => {
  const { isMobile } = useScreenSize();

  const queryResponse = useStaticQuery(graphql`
    query LastArticles {
      allSanityArticles(sort: {_createdAt: DESC}, limit: 6) {
        ...ArticlesPreviewList
      }
    }
  `);

  const lastArticles = queryResponse.allSanityArticles.nodes as ArticlePreviewType[];
  const articles = data.highlight
    ? (lastArticles.filter(item => !data.highlight!.some(article => article._id === item._id))).slice(0, 4)
    : lastArticles.slice(0, 4);

  const mobileList = [...(data.highlight ? data.highlight.map(item => ({ ...item, highlight: true })) : []), ...articles.map(item => ({ ...item, highlight: false }))];

  if (isMobile) {
    return (
      <section className='news-home' id={data.htmlId || undefined}>
        <div className='news-home-header'>
          <div>
            <h2>{data.title ? data.title : 'News'}</h2>
            {data.description && (
              <p>{data.description}</p>
            )}
          </div>
          <InternalLink reference={data.pageReference}>
            <InternalLinkIcon />
          </InternalLink>
        </div>

        <div className='news-home-list'>
          {mobileList.map(article => (
            <ArticlePreview key={article._id} article={article} />
          ))}
        </div>
      </section>
    );
  }

  return (
    <section className='news-home' id={data.htmlId || undefined}>
      <div className='news-home-header'>
        <h2>{data.title ? data.title : 'News'}</h2>
        {data.description && (
          <p>{data.description}</p>
        )}
      </div>

      <div className='news-home-grid'>
        <div>
          <div className='news-home-list'>
            {articles.map(article => (
              <ArticleCard
                key={article._id}
                article={article}
              />
            ))}

            <InternalLink
              reference={data.pageReference}
              className='primary-button'
            >
              {data.cta ? data.cta : 'See all news'}
              <InternalLinkIcon />
            </InternalLink>
          </div>
        </div>

        <div className='news-home-highlight'>
          {data.highlight && (
            <>
              <strong>
                {data.highlightTitle ? data.highlightTitle : 'Highlighted content'}
              </strong>
              <div>
                {data.highlight.map(article => (
                  <HighlichtCard
                    key={article._id}
                    article={article}
                  />
                ))}
              </div>
            </>
          )}
        </div>
      </div>

    </section>
  );
}

export default NewsHome;
